import styled from 'styled-components';
import { LayoutConst } from 'core';
import { StyledDateTimePicker } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/styles';

export const ModalWrapper = styled.div`
  width: 720px;
  max-width: 720px;
  max-height: min(90vh, calc(100vh - 48px));
  overflow-y: auto;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #ffffff;
  border-radius: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    max-width: calc(100vw - 16px);
    padding: 20px 16px;
    gap: 16px;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

export const Title = styled.h2`
  margin: 0;
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
  color: #151514;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 25px;
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  color: #909090;
  font-size: 36px;
  line-height: 28px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const Row = styled.div<{ $columns?: number; $alignItems?: 'start' | 'end' }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
  align-items: ${({ $alignItems = 'start' }) => $alignItems};
  gap: 24px;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.s}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const Dropdown = styled.div`
  .ui-input_container {
    height: 48px !important;
    background: #f2f2f2;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    .ui-input_container {
      height: 40px !important;
    }
  }
`;

export const ModalDatePicker = styled(StyledDateTimePicker)`
  width: 100%;

  .react-datetime-picker__wrapper {
    background-color: #f2f2f2;
  }
`;

export const TextInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 14px 16px;
  border: none;
  border-radius: 3px;
  outline: none;
  background: #f2f2f2;
  color: #151514;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;

  &::placeholder {
    color: #bfbfbf;
  }

  &:disabled {
    color: #909090;
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: 40px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 16px;
  }
`;

export const FieldHint = styled.div`
  font-family: Inter;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.26px;
  color: #909090;
`;

export const TimeWrapper = styled.div`
  position: relative;

  input {
    padding-right: 48px;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }
`;

export const WeekdaysRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-radius: 3px;
  overflow: hidden;
  width: fit-content;
  background: #e7e7e7;
`;

export const WeekdayButton = styled.button<{ $active: boolean; $filled: boolean }>`
  width: 48px;
  height: 48px;
  border: none;
  padding: 0;
  background: ${({ $active, $filled }) => ($active ? '#29894E' : $filled ? '#B4D1BF' : '#E7E7E7')};
  color: ${({ $active, $filled }) => ($active ? '#ffffff' : $filled ? '#343434' : '#909090')};
  cursor: pointer;
  font-family: Inter;
  font-size: 17px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  line-height: 22px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const DaysControls = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.m}px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const ClearDayButton = styled.button`
  flex-shrink: 0;
  height: 48px;
  padding: 0 24px;
  border: 1px solid #151514;
  border-radius: 3px;
  background: #ffffff;
  color: #151514;
  cursor: pointer;
  font-family: Inter;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
`;

export const Chip = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 5px 16px;
  border: 1px solid ${({ $selected }) => ($selected ? '#838382' : '#909090')};
  border-radius: 5px;
  background: ${({ $selected }) => ($selected ? '#838382' : '#ffffff')};
  color: ${({ $selected }) => ($selected ? '#ffffff' : '#151514')};
  cursor: pointer;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const DropdownFixedField = styled(Field)`
  .dropdown-list-item {
    height: auto;
    min-height: 48px;
  }
`import styled from 'styled-components';
import { LayoutConst } from 'core';
import { StyledDateTimePicker } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/styles';

export const ModalWrapper = styled.div`
  width: 720px;
  max-width: 720px;
  max-height: min(90vh, calc(100vh - 48px));
  overflow-y: auto;
  padding: 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #ffffff;
  border-radius: 8px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${LayoutConst.screen.m}px) {
    max-width: calc(100vw - 16px);
    padding: 20px 16px;
    gap: 16px;
  }
`;

export const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

export const Title = styled.h2`
  margin: 0;
  font-family: Inter;
  font-size: 28px;
  font-weight: 600;
  line-height: 34px;
  letter-spacing: -0.56px;
  color: #151514;

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 20px;
    line-height: 25px;
  }
`;

export const Subtitle = styled.p`
  margin: 0;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;
  color: #909090;
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  color: #909090;
  font-size: 36px;
  line-height: 28px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const Row = styled.div<{ $columns?: number; $alignItems?: 'start' | 'end' }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 2 }) => $columns}, minmax(0, 1fr));
  align-items: ${({ $alignItems = 'start' }) => $alignItems};
  gap: 24px;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.s}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const Dropdown = styled.div`
  .ui-input_container {
    height: 48px !important;
    background: #f2f2f2;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    .ui-input_container {
      height: 40px !important;
    }
  }
`;

export const ModalDatePicker = styled(StyledDateTimePicker)`
  width: 100%;

  .react-datetime-picker__wrapper {
    background-color: #f2f2f2;
  }
`;

export const TextInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 14px 16px;
  border: none;
  border-radius: 3px;
  outline: none;
  background: #f2f2f2;
  color: #151514;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;

  &::placeholder {
    color: #bfbfbf;
  }

  &:disabled {
    color: #909090;
    cursor: not-allowed;
    opacity: 0.7;
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    height: 40px;
    padding: 10px 14px;
    font-size: 13px;
    line-height: 16px;
  }
`;

export const FieldHint = styled.div`
  font-family: Inter;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.26px;
  color: #909090;
`;

export const TimeWrapper = styled.div`
  position: relative;

  input {
    padding-right: 48px;

    &::-webkit-calendar-picker-indicator {
      display: none;
    }
  }
`;

export const WeekdaysRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-radius: 3px;
  overflow: hidden;
  width: fit-content;
  background: #e7e7e7;
`;

export const WeekdayButton = styled.button<{ $active: boolean; $filled: boolean }>`
  width: 48px;
  height: 48px;
  border: none;
  padding: 0;
  background: ${({ $active, $filled }) => ($active ? '#29894E' : $filled ? '#B4D1BF' : '#E7E7E7')};
  color: ${({ $active, $filled }) => ($active ? '#ffffff' : $filled ? '#343434' : '#909090')};
  cursor: pointer;
  font-family: Inter;
  font-size: 17px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  line-height: 22px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const DaysControls = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  width: 100%;

  @media (max-width: ${LayoutConst.screen.m}px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const ClearDayButton = styled.button`
  flex-shrink: 0;
  height: 48px;
  padding: 0 24px;
  border: 1px solid #151514;
  border-radius: 3px;
  background: #ffffff;
  color: #151514;
  cursor: pointer;
  font-family: Inter;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
`;

export const Chip = styled.button<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 5px 16px;
  border: 1px solid ${({ $selected }) => ($selected ? '#838382' : '#909090')};
  border-radius: 5px;
  background: ${({ $selected }) => ($selected ? '#838382' : '#ffffff')};
  color: ${({ $selected }) => ($selected ? '#ffffff' : '#151514')};
  cursor: pointer;
  font-family: Inter;
  font-size: 15px;
  line-height: 20px;
  letter-spacing: -0.3px;

  &:focus,
  &:focus-visible {
    outline: none;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const DropdownFixedField = styled(Field)`
  .dropdown-list-item {
    height: auto;
    min-height: 48px;
  }
`